import { Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";
import { faArrowLeftRotate, faBoxesPacking, faEllipsis, faFlask, faHouse, faLeaf, faPlantWilt, faPlus, faRecycle, faSeedling, faSprayCan, faWheatAlt } from "@fortawesome/free-solid-svg-icons";

//ASSETS
import iconTalhao from '../../../../assets/img/iconTalhao.svg';

//APP
import secureLocalStorage from "react-secure-storage";
import { localConfig } from "../../../../Configs/localConfig";
import { useGetOsColetada } from "../../../../services/Laboratorio/GET";
import { getCodTipoColeta, TipoColeta } from "./meta";
import { montarRelatorioSolo, montarTotaisSolo } from "./modulos/solo/meta/relatorio";
import { motivoSemPermissao, usePermissoesOs } from "./meta/usePermissoesOs";
import { ordenarListaOs, type EstadoOrdenacao } from "./modulos/solo/meta/ordenarOs";
import { exibirClienteNaLinha } from "./modulos/solo/meta/acessoCliente";
import { Item__Talhao, useGetPropriedadesDoCliente } from "../../../../services/atividades/GET";

//COMPONENTS
import Input from "../../../../Components/CustomUi/input";
import ButtonCustom from "../../../../Components/ButtonCustom";
import GeneratePdfButton from "../../../../Components/relatorio-component";
import { Solo } from "./modulos/solo";
import { Render } from "../../../../Components/CustomUi/render";
import { Inline } from "../../../../Components/CustomUi/inline";
import { PopSelect } from "../../../../Components/CustomUi/PopSelect";
import { DefautContainer } from "../../../../Components/CustomUi/defaultContainer";
import { Link } from "react-router-dom";

//CONSTs
const ICON_LIST_TALHAO = ["kml", "coordenadas"];
const TABS_CONFIG = [
    { descricao: 'Solo', icone: faWheatAlt, peso: 1 },
    { descricao: 'Plantas', icone: faSeedling, peso: 1.2 },
    { descricao: 'Folhas', icone: faLeaf, peso: 1.1 },
    { descricao: 'Raízes', icone: faPlantWilt, peso: 1.1 },
    { descricao: 'Microbiológica', icone: faFlask, peso: 1.7 },
    { descricao: 'Fertilizantes', icone: faBoxesPacking, peso: 1.5 },
    { descricao: 'Corretivos', icone: faSprayCan, peso: 1.3 },
    { descricao: 'Composto orgânico', icone: faRecycle, peso: 1.9 },
    { descricao: 'Outro', icone: faEllipsis, peso: 1 },
]

//#region APP
export const NovoModuloOsParaColeta: React.FC = () => {

    //#region STATES
    /**
     * Pedido de "nova OS" por submódulo — o botão "Novo" fica aqui no header, mas
     * a gaveta é de cada submódulo. Um estado por tipo de coleta (não um só
     * compartilhado): cada submódulo controla a própria gaveta e as regras não se
     * misturam. Novo tipo = novo estado + case no dispatcher.
     */
    const [abrirNovaOsSolo, setAbrirNovaOsSolo] = useState<boolean>(false);

    //CONST
    const { codSafra } = localConfig();
    const permissoes = usePermissoesOs();

    //REF
    const containerRef = useRef<HTMLDivElement>(null)

    //#region STATES
    //PROPRIEDADE
    const [propriedade_selecionada, setPropriedade_selecionada] = useState<string>('');
    const [propriedade_selecionada_id, setPropriedade_selecionada_id] = useState<number | string>(null);
    //TALHÃO
    const [lista_talhoes, setLista_talhoes] = useState<Item__Talhao[]>([]);
    const [talhao_selecionado, setTalhao_selecionado] = useState<string>('');
    const [talhao_selecionado_id, setTalhao_selecionado_id] = useState<number | string>(null);
    //BUSCAR
    const [buscar_tabela, setBuscar_tabela] = useState<string>('');
    // Valor "atrasado" da busca: é ele que entra na queryKey, para não disparar
    // uma requisição a cada tecla digitada.
    const [buscar_debounced, setBuscar_debounced] = useState<string>('');
    //TIPO DE COLETA
    const [tipo_coleta, setTipo_coleta] = useState<TipoColeta>('Solo');
    // Ordenação da tabela: vive no pai porque é ele quem detém a lista completa e
    // faz o recorte da página — ordenar dentro do submódulo pegaria só a fatia.
    const [ordenacao, setOrdenacao] = useState<EstadoOrdenacao | null>(null);
    //CONTAINER
    const [containerWidth, setContainerWidth] = useState<number>(0);
    /**
     * Itens por página — usado na paginação CLIENT-SIDE (ver `lista_os_pagina`).
     * A requisição traz a lista inteira (`pagination = -1` no service): filtro por
     * talhão e ordenação são feitos aqui, e paginar no servidor faria os dois
     * agirem só sobre a fatia visível (a API não aceita filtro por talhão nem
     * ordenação).
     */
    const [pagination, setPagination] = useState<number>(15);
    const [page, setPage] = useState<number>(1);
    // Loading curto e forçado na troca de página: quando a página vem do cache
    // não há requisição, mas a re-renderização das linhas ainda custa alguns
    // frames. Sem nenhum sinal em tela isso é percebido como travada.
    const [fake_loading, setFake_loading] = useState<boolean>(false);

    // Código do tipo de coleta ativo no backend (Solo=282; demais tipos conforme liberados).
    const cod_tipo_coleta = getCodTipoColeta(tipo_coleta) ?? undefined;

    //#region API
    // A OS lista TODAS as propriedades, inclusive as sem talhão na safra — regra
    // própria deste módulo; as telas de atividades seguem com o corte por safra.
    const { data_propriedades, carregando_propriedades } = useGetPropriedadesDoCliente({ somenteComTalhaoNaSafra: false }); //lista as propriedades com os talhões
    // -1 = lista completa numa requisição só. A paginação é client-side, então o
    // `page` local não entra aqui: mudar de página não dispara requisição nova.
    const { data_osColeta, carregando_osColeta, buscando_osColeta, refetch_data_osColeta } = useGetOsColetada(-1, 1, '', codSafra, Number(propriedade_selecionada_id), cod_tipo_coleta);

    //#region LISTA MEMOs
    // Memoiza para não recriar arrays a cada render (quebraria o memo do PopSelect/SvgTalhaoMemo)
    const propriedades_ativas = useMemo(
        () => data_propriedades?.filter(i => i.status === 1),
        [data_propriedades]
    );
    /**
     * Filtro repassado à gaveta de cadastro, que pré-preenche a propriedade com
     * ele. Memoizado porque é dependência de efeito no formulário — um objeto
     * novo a cada render faria o preenchimento reexecutar à toa.
     */
    const filtro_propriedade = useMemo(
        () => ({ cod: Number(propriedade_selecionada_id) || null, nome: propriedade_selecionada }),
        [propriedade_selecionada_id, propriedade_selecionada]
    );

    /**
     * Talhões da propriedade escolhida no filtro.
     *
     * Sem recorte por safra: a listagem passou a oferecer todas as propriedades,
     * e cortar os talhões aqui deixaria o select vazio justamente naquelas que
     * não têm talhão na safra. O `cod_talhao === null` continua excluindo
     * subáreas, que não são unidade de coleta.
     */
    const talhoes_filtrados = useMemo(
        () => (lista_talhoes ?? []).filter(i => i.cod_talhao === null),
        [lista_talhoes]
    );
    /**
     * Sem cliente no filtro global a listagem mistura vários clientes.
     *
     * Filtrar por propriedade/talhão nesse estado não se sustenta: as listas
     * viriam de clientes diferentes. Os dois selects ficam desabilitados e a
     * busca por nome é o caminho. Também é a condição da tag de cliente exibida
     * na coluna "Propriedade".
     */
    const sem_cliente_global = exibirClienteNaLinha(localConfig()?.codCliente);

    /**
     * Filtros aplicados no cliente: a lista já vem inteira (`pagination = -1`), então
     * buscar no servidor só adicionaria latência a cada tecla.
     *
     * A busca cobre propriedade, talhão e cliente — os três textos visíveis na
     * linha (o cliente aparece como tag na coluna "Propriedade"). Sem cliente no
     * filtro global os selects ficam desabilitados, e esta busca é o único
     * caminho para achar uma OS.
     */
    const lista_os_filtrada = useMemo(() => {
        const busca = buscar_debounced.trim().toLowerCase();

        return (data_osColeta?.itens ?? [])
            .filter(i => !talhao_selecionado || i.talhao_nome === talhao_selecionado)
            .filter(i => {
                if (!busca) return true;

                return [i?.propriedade_nome, i?.talhao_nome, i?.cliente_nome]
                    .some(campo => String(campo ?? '').toLowerCase().includes(busca));
            });
    }, [data_osColeta, talhao_selecionado, buscar_debounced]);
    /**
     * Ordem: FILTRAR -> ORDENAR -> FATIAR. A ordenação precisa acontecer antes do
     * recorte, senão reordenaria apenas os itens já visíveis na página.
     */
    const lista_os_ordenada = useMemo(
        () => ordenarListaOs(lista_os_filtrada, ordenacao),
        [lista_os_filtrada, ordenacao]
    )
    /** Fatia exibida na página atual (paginação client-side). */
    const lista_os_pagina = useMemo(() => {
        const inicio = (page - 1) * pagination
        return lista_os_ordenada.slice(inicio, inicio + pagination)
    }, [lista_os_ordenada, page, pagination])
    //#endregion


    // Filtrar ou trocar o tamanho da página pode deixar `page` além do fim da
    // lista — sem isso a tabela ficaria vazia até o usuário voltar à mão.
    useEffect(() => {
        const ultimaPagina = Math.max(1, Math.ceil(lista_os_filtrada.length / pagination))
        if (page > ultimaPagina) setPage(ultimaPagina)
    }, [lista_os_filtrada.length, pagination, page])

    //#region RELATÓRIO PDF
    /**
     * O relatório cobre TODAS as OS da propriedade (sem o filtro de talhão) e é
     * montado pelo arquivo de relatório do submódulo ativo — cada tipo de coleta
     * declara as próprias colunas, sem condicional acumulada aqui.
     */
    const lista_os_relatorio = useMemo(() => data_osColeta?.itens ?? [], [data_osColeta])

    const dados_relatorio = useMemo(() => {
        switch (tipo_coleta) {
            case 'Solo':
                return montarRelatorioSolo(lista_os_relatorio)
            default:
                return []
        }
    }, [tipo_coleta, lista_os_relatorio])

    const totais_relatorio = useMemo(() => {
        switch (tipo_coleta) {
            case 'Solo':
                return montarTotaisSolo(lista_os_relatorio)
            default:
                return []
        }
    }, [tipo_coleta, lista_os_relatorio])

    /**
     * Subtítulo do PDF: cliente + propriedade. Sem propriedade selecionada o
     * relatório cobre a safra inteira, então o escopo precisa ficar explícito —
     * caso contrário o PDF pareceria ser de uma propriedade só.
     */
    const subtitulo_relatorio = useMemo(() => {
        const cliente = lista_os_relatorio?.[0]?.nome_cliente ?? ''
        const escopo = propriedade_selecionada || 'Todas as propriedades'
        return [cliente, escopo].filter(Boolean).join(' - ')
    }, [lista_os_relatorio, propriedade_selecionada])

    /** Produção libera o relatório apenas para acesso do tipo "conta". */
    const pode_gerar_relatorio = secureLocalStorage.getItem('tipo-acesso') === 'conta'

    //#region FUNCTIONS
    /**
     * Abre a gaveta de "nova OS" do submódulo correspondente ao tipo de coleta ativo.
     * Cada submódulo é dono da própria gaveta e a recebe por prop.
     * Preparado para escalar: basta registrar o estado + case do novo tipo.
     */
    function abrirFormularioNovaOs() {
        switch (tipo_coleta) {
            case 'Solo':
                setAbrirNovaOsSolo(true);
                break;
            // case 'Plantas': setAbrirNovaOsPlanta(true); break;
            // case 'Folhas': setAbrirNovaOsFolha(true); break;
            // ...demais submódulos conforme forem implementados
            default:
                break;
        }
    }

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth)
            }
        }
        updateWidth()
        window.addEventListener('resize', updateWidth)
        return () => window.removeEventListener('resize', updateWidth)
    }, []);

    useEffect(() => {
        // Trocar (ou limpar) a propriedade invalida o talhão escolhido: ele
        // pertencia à propriedade anterior. O id também é zerado, senão sobraria
        // um filtro sem rótulo correspondente.
        setTalhao_selecionado(null);
        setTalhao_selecionado_id(null);

        /**obtem a lista de talhões da propriedade selecionada */
        if ((data_propriedades ?? []).length > 0) {
            let propriedade_selecionada = (data_propriedades ?? []).filter(i => i?.cod === propriedade_selecionada_id);
            setLista_talhoes(propriedade_selecionada?.[0]?.talhao ?? []);
        }
    }, [propriedade_selecionada_id]);

    // Aguarda a digitação parar antes de refiltrar. Não há requisição envolvida
    // (o filtro é local), mas a lista pode ter centenas de linhas e refiltrar a
    // cada tecla trava a digitação.
    useEffect(() => {
        const timer = setTimeout(() => setBuscar_debounced(buscar_tabela), 300);
        return () => clearTimeout(timer);
    }, [buscar_tabela]);

    // Volta para a primeira página sempre que o recorte da consulta muda,
    // senão a página atual pode não existir no novo resultado.
    useEffect(() => {
        setPage(1);
    }, [propriedade_selecionada_id, tipo_coleta, buscar_debounced]);

    // Sem efeito de refetch manual: propriedade e tipo de coleta fazem parte da
    // queryKey, então o React Query busca sozinho. Busca por texto e paginação
    // são resolvidas localmente e não disparam requisição.

    // Mostra o loading por um instante ao paginar. Não é o tempo da requisição
    // (páginas em cache não fazem nenhuma) e sim o custo de montar as linhas,
    // que sem feedback nenhum parece travamento.
    const primeira_renderizacao = useRef<boolean>(true);
    useEffect(() => {
        if (primeira_renderizacao.current) {
            primeira_renderizacao.current = false;
            return;
        }
        setFake_loading(true);
        const timer = setTimeout(() => setFake_loading(false), 400);
        return () => clearTimeout(timer);
    }, [page, pagination]);


    /**
    * Calcula a largura de cada tab proporcionalmente
    * para preencher 100% da largura do container.
    * O padding interno do ListTabs (10px = 5px * 2) é descontado.
    */
    function calcularLarguras() {
        const larguraDisponivel = containerWidth - 10 // padding do ListTabs
        const somaPesos = TABS_CONFIG.reduce((acc, tab) => acc + tab.peso, 0)

        return TABS_CONFIG.map((tab) => ({
            descricao: tab.descricao,
            icone: tab.icone,
            width: Math.floor((tab.peso / somaPesos) * larguraDisponivel),
        }))
    }

    // const tabsComLargura = calcularLarguras()

    //#region APP
    return (
        <DefautContainer
            title="Os para coleta"
            id_safra="precisao/os-para-coleta"
        >
            <Link to={'/precisao/laborder-legado'} style={{
                position: 'absolute',
                right: 32,
                top: 108,
                fontSize: 12,
                textDecoration: 'underline',
                color: 'var(--primary-color)',
            }}>
                <FontAwesomeIcon icon={faArrowLeftRotate} />{' '}
                Interface antiga
            </Link>
            <Inline alignItems="flex-end" refProp={containerRef}>
                {/* Sem cliente global as propriedades seriam de clientes diferentes:
                    o filtro fica bloqueado e a busca por nome é o caminho. */}
                <Tooltip title={sem_cliente_global ? 'Selecione um cliente no menu superior para filtrar por propriedade' : undefined}><samp style={{ flex: '0 1 auto', minWidth: 0, maxWidth: '25%', display: 'block' }}>
                    <PopSelect
                        label='Propriedade'
                        empty_placeholder='Selecione a propriedade'
                        value={propriedade_selecionada}
                        icon={faHouse}
                        onChange={setPropriedade_selecionada}
                        onChangeId={setPropriedade_selecionada_id}
                        items={propriedades_ativas}
                        descricaoItem={'nome'}
                        idItem={'cod'}
                        disabled={sem_cliente_global}
                        loading={carregando_propriedades}
                    /></samp>
                </Tooltip>

                {/* Filtrar por talhão exige uma propriedade: a lista de talhões vem dela. */}
                <Tooltip title={sem_cliente_global ? 'Selecione um cliente no menu superior para filtrar por talhão' : (propriedade_selecionada_id === null ? 'Selecione uma propriedade para filtrar por talhão' : undefined)}><samp style={{ flex: '0 1 auto', minWidth: 0, maxWidth: '25%', display: 'block' }}>
                    <PopSelect
                        label="Selecione o talhão"
                        empty_placeholder="Nenhum foi selecionado"
                        icon={iconTalhao}
                        value={talhao_selecionado}
                        onChange={setTalhao_selecionado}
                        onChangeId={setTalhao_selecionado_id}
                        items={talhoes_filtrados}
                        iconList={ICON_LIST_TALHAO}
                        maxPontosSvg={500}
                        descricaoItem="nome"
                        idItem="cod"
                        extraItem='area'
                        disabled={sem_cliente_global || propriedade_selecionada_id === null}
                        zIndex={9}
                        loading={carregando_propriedades}
                    /></samp>
                </Tooltip>

                <div style={{ flex: '1 1 0', minWidth: 180 }}>
                    <Input
                        placeholder='Buscar OS'
                        value={buscar_tabela}
                        onChange={setBuscar_tabela}
                    />
                </div>

                {/* Listar não exige propriedade, mas CRIAR sim: a OS pertence a uma
                    propriedade e a um talhão dela. */}
                <Tooltip
                    title={
                        motivoSemPermissao(permissoes?.inclusao !== 0, 'criar OS') ??
                        (propriedade_selecionada_id === null
                            ? 'Selecione uma propriedade para criar uma OS'
                            : undefined)
                    }
                >
                    <span style={{ marginBottom: 7, display: 'inline-block' }}>
                        <ButtonCustom
                            icon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={abrirFormularioNovaOs}
                            disabled={permissoes?.inclusao === 0}
                        >
                            Cadastrar
                        </ButtonCustom>
                    </span>
                </Tooltip>

                <Render condition={pode_gerar_relatorio}>
                    <div style={{ marginBottom: 7 }}>
                        <GeneratePdfButton
                            title='Relatório O.S'
                            titleSecundario={subtitulo_relatorio}
                            dados={dados_relatorio}
                            totais={totais_relatorio}
                            disable={lista_os_relatorio.length === 0}
                        />
                    </div>
                </Render>
            </Inline>

            {/* <Tabs
                selecionado={tipo_coleta}
                onChange={setTipo_coleta}
                tabs={tabsComLargura}
                useTabWidth
            /> */}

            {/* A propriedade virou filtro OPCIONAL: sem ela a lista traz as OS de
                todas as propriedades da safra, então não há mais estado vazio de
                nível de página — o "nenhuma OS cadastrada" é do submódulo. */}
            {tipo_coleta === 'Solo' && (
                <Solo
                    abrirNovaOs={abrirNovaOsSolo}
                    setAbrirNovaOs={setAbrirNovaOsSolo}
                    lista_os={lista_os_pagina}
                    ordenacao={ordenacao}
                    setOrdenacao={setOrdenacao}
                    refetch_data_osColeta={refetch_data_osColeta}
                    loading={carregando_osColeta}
                    buscando={buscando_osColeta || fake_loading}
                    total={lista_os_filtrada.length}
                    total_filtrado={talhao_selecionado ? lista_os_filtrada.length : 0}
                    page={page}
                    setPage={setPage}
                    setPagination={setPagination}
                    filtro_propriedade={filtro_propriedade}
                />
            )}

        </DefautContainer>
    )
}
//#endregion